import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Archive, ChevronDown, Loader2, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { WallpapersResponse } from '@/lib/collections/wallpapers';

interface AdminArchiveWallpapersProps {
  isOpen: boolean;
  onClose: () => void;
  /** Wallpaper category names (already excludes 'All'). Sourced from the admin-managed category list. */
  categories: string[];
  /** Candidate wallpapers to archive from — mediaType 'wallpaper' only, tombstoned ('deleted') items already excluded by the caller. */
  wallpapers: WallpapersResponse[];
}

const CONCURRENCY = 4;

// Mirrors the full-resolution URL logic in use-wallpaper-download.ts — imageUrl is
// always the full-res asset (thumbnailUrl points at the same S3 object and ignores
// ?w= resize params), so archive downloads always pull imageUrl at max quality.
function getFullImageUrl(wallpaper: WallpapersResponse): string {
  const baseUrl = wallpaper.imageUrl;
  const stripped = baseUrl.split('?')[0];
  const isUnsplash = stripped.includes('unsplash.com') || stripped.includes('images.unsplash');
  return isUnsplash ? `${stripped}?w=2160&q=90&auto=format` : baseUrl;
}

function getExtension(wallpaper: WallpapersResponse): string {
  const stripped = getFullImageUrl(wallpaper).split('?')[0];
  const match = stripped.match(/\.(\w{2,4})$/);
  return match ? match[1].toLowerCase() : 'jpg';
}

async function runWithConcurrency<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
  let cursor = 0;
  const runners = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      await worker(item);
    }
  });
  await Promise.all(runners);
}

export const AdminArchiveWallpapers: React.FC<AdminArchiveWallpapersProps> = ({ isOpen, onClose, categories, wallpapers }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failCount, setFailCount] = useState(0);
  const [lastResult, setLastResult] = useState<{ archived: number; failed: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCategory(prev => (prev && categories.includes(prev)) ? prev : (categories[0] ?? ''));
      setLastResult(null);
      setFailCount(0);
      setProgress({ done: 0, total: 0 });
    }
  }, [isOpen, categories]);

  const itemsInCategory = useMemo(
    () => wallpapers.filter(w => w.category === selectedCategory),
    [wallpapers, selectedCategory]
  );

  const handleClose = () => {
    if (isArchiving) return;
    onClose();
  };

  const handleArchive = async () => {
    if (isArchiving || itemsInCategory.length === 0) return;

    setIsArchiving(true);
    setLastResult(null);
    setFailCount(0);
    setProgress({ done: 0, total: itemsInCategory.length });

    const zip = new JSZip();
    const nameCounts = new Map<string, number>();
    let archived = 0;
    let failed = 0;
    let done = 0;

    await runWithConcurrency(itemsInCategory, CONCURRENCY, async (wallpaper) => {
      try {
        const url = getFullImageUrl(wallpaper);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();

        const ext = getExtension(wallpaper);
        const base = (wallpaper.title ?? wallpaper.id).trim() || wallpaper.id;
        const count = nameCounts.get(base) ?? 0;
        nameCounts.set(base, count + 1);
        const filename = count === 0 ? `${base}.${ext}` : `${base}-${count + 1}.${ext}`;

        zip.file(filename, blob);
        archived += 1;
      } catch (err) {
        console.error(`Failed to fetch wallpaper for archive: ${wallpaper.id}`, err);
        failed += 1;
      } finally {
        done += 1;
        setProgress({ done, total: itemsInCategory.length });
        setFailCount(failed);
      }
    });

    if (archived === 0) {
      setIsArchiving(false);
      toast.error('Failed to archive any wallpapers in this category — please try again');
      return;
    }

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      const blobUrl = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${selectedCategory}-wallpapers-archive.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Failed to build archive zip', err);
      toast.error('Failed to build the zip file — please try again');
      setIsArchiving(false);
      return;
    }

    setLastResult({ archived, failed });
    setIsArchiving(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 3100,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(16px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 380,
              background: '#0a0a18',
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.08)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '14px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                background: 'rgba(34,211,238,0.12)',
                border: '1px solid rgba(34,211,238,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Archive size={16} color="#22d3ee" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#f0f0f5', fontFamily: "'Syne', sans-serif" }}>
                  Archive Category
                </div>
                <div style={{ fontSize: 10, color: 'rgba(240,240,245,0.35)', fontFamily: "'DM Mono', monospace" }}>
                  Download full-res wallpapers as a zip
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                disabled={isArchiving}
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isArchiving ? 'not-allowed' : 'pointer',
                  color: 'rgba(240,240,245,0.6)',
                  flexShrink: 0,
                }}
              >
                <X size={13} />
              </motion.button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px' }}>
              <label style={{
                display: 'block', fontSize: 10, fontWeight: 700, color: 'rgba(240,240,245,0.4)',
                textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
                fontFamily: "'Syne', sans-serif",
              }}>
                Category
              </label>
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  disabled={isArchiving || categories.length === 0}
                  style={{
                    width: '100%',
                    background: 'rgba(34,211,238,0.1)',
                    border: '1px solid rgba(34,211,238,0.3)',
                    color: '#f0f0f5', fontSize: 13, fontWeight: 700,
                    fontFamily: "'DM Mono', monospace",
                    borderRadius: 9, padding: '10px 12px',
                    appearance: 'none', paddingRight: 30,
                    outline: 'none', cursor: isArchiving ? 'not-allowed' : 'pointer', boxSizing: 'border-box',
                  }}
                >
                  {categories.length === 0 && <option value="">No categories</option>}
                  {categories.map(cat => (
                    <option key={cat} value={cat} style={{ background: '#0b0b17' }}>{cat}</option>
                  ))}
                </select>
                <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(240,240,245,0.35)', pointerEvents: 'none' }} />
              </div>

              <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.35)', fontFamily: "'DM Mono', monospace", marginTop: 8 }}>
                {itemsInCategory.length} wallpaper{itemsInCategory.length !== 1 ? 's' : ''} in this category
              </div>

              {/* Progress */}
              {isArchiving && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'DM Mono', monospace" }}>
                      Fetching images...
                    </span>
                    <span style={{ fontSize: 11, color: '#67e8f9', fontFamily: "'DM Mono', monospace" }}>
                      {progress.done} / {progress.total}
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                    <motion.div
                      animate={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
                      transition={{ duration: 0.2 }}
                      style={{ height: '100%', background: 'linear-gradient(90deg, #0891b2, #22d3ee)', borderRadius: 2 }}
                    />
                  </div>
                  {failCount > 0 && (
                    <div style={{ fontSize: 10, color: 'rgba(248,113,113,0.7)', fontFamily: "'DM Mono', monospace", marginTop: 5 }}>
                      {failCount} skipped so far
                    </div>
                  )}
                </div>
              )}

              {/* Result summary */}
              {!isArchiving && lastResult && (
                <div style={{
                  marginTop: 14, padding: '9px 11px', borderRadius: 9,
                  background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.2)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <PackageOpen size={14} color="#67e8f9" />
                  <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.7)', fontFamily: "'DM Mono', monospace" }}>
                    Archived {lastResult.archived}{lastResult.failed > 0 ? `, skipped ${lastResult.failed}` : ''}
                  </span>
                </div>
              )}

              {/* Download button */}
              <motion.button
                whileHover={(isArchiving || itemsInCategory.length === 0) ? {} : { scale: 1.01 }}
                whileTap={(isArchiving || itemsInCategory.length === 0) ? {} : { scale: 0.99 }}
                onClick={handleArchive}
                disabled={isArchiving || itemsInCategory.length === 0}
                style={{
                  width: '100%', marginTop: 14,
                  padding: '11px', borderRadius: 10,
                  background: (isArchiving || itemsInCategory.length === 0) ? 'rgba(34,211,238,0.25)' : 'rgba(34,211,238,0.85)',
                  border: '1px solid rgba(103,232,249,0.4)',
                  color: (isArchiving || itemsInCategory.length === 0) ? 'rgba(255,255,255,0.55)' : '#04222a',
                  fontSize: 14, fontWeight: 700,
                  cursor: (isArchiving || itemsInCategory.length === 0) ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.15s ease',
                }}
              >
                {isArchiving ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Building zip...
                  </>
                ) : (
                  <>
                    <Archive size={15} />
                    {itemsInCategory.length > 0 ? 'Download Zip' : 'No wallpapers to download'}
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
